import React, {FC, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    EXTENSION_TYPE_DIRECTIMPORT,
    EXTENSION_TYPE_EXPORT,
    EXTENSION_TYPE_IMPORT,
    EXTENSION_TYPE_PROCUREMENT,
    EXTENSION_TYPE_REALTIME,
    EXTENSION_TYPE_WEBHOOK,
    IAdditionalData,
    IExtension,
    IExtensionInstallRequest,
    IExtensionOption,
    IImportMapping,
    IInstalledExtensionFolio,
    IValidateExtensionResponse,
} from "../../models/Extension";
import {
    Checkbox,
    DefaultButton,
    Dialog,
    DialogFooter,
    DialogType,
    Dropdown,
    Icon,
    IconButton,
    Label,
    Link,
    MessageBar,
    MessageBarType,
    PrimaryButton,
    SpinButton,
    Spinner,
    SpinnerSize,
    Stack,
    StackItem,
    Text,
    TextField,
    TooltipHost
} from "@fluentui/react";

/**
 * The "needs your input" band's surface.
 *
 * Green rather than the neutral it started as: the band sits directly under the capability accordion's
 * grey header (#f2f2f2 in App.css), and a light-grey panel with a grey border against that read as two
 * headers stacked on each other. A different hue separates them without adding weight.
 *
 * Hand-picked rather than taken from the palette because Fluent v8 has no light-green surface pair — the
 * only green in the theme is `semanticColors.successBackground`, which is the MessageBar's treatment and
 * carries a "this succeeded" meaning that is wrong for settings that are unfinished. These are the same
 * green family, several steps lighter, so it reads as a quiet area of the form and not as a status.
 */
const OUTSTANDING_BAND_BACKGROUND = '#f4faf2';
const OUTSTANDING_BAND_BORDER = '#cbe5c4';
import MarkdownView from "react-showdown";
import MappingTable from "./MappingTable";
import {
    useGetCatalogsQuery,
    useGetExtensionFolioQuery,
    useInstallExtensionMutation,
    useLazyGetExtensionOptionsBatchQuery,
    useLazyGetImportSampleQuery,
    usePutExtensionFolioMutation,
    usePutInstalledExtensionMutation, useRunImportExtensionMutation,
    useValidateExtensionFolioMutation,
    useValidateExtensionSettingsMutation,
    useValidateInstalledExtensionMutation
} from "../../store/Api";
import {IActionResponse} from "../../dal/BaseDAL";
import {IsMutationError, IsMutationSuccess} from "../../logic/MutationTypeChecker";
import ExtensionInstallOption from "./ExtensionInstallOption";
import Scheduler, {ConvertCronToSchedule, ConvertScheduleToCron, ISchedule} from "../Scheduler/Scheduler";
import {cloneDeep, debounce} from "lodash";
import ExtensionFieldSearch from "../ExtensionModal/ExtensionFieldSearch";
import ExtensionDependentOption from "./ExtensionDependentOption";
import ExtensionDynamicFieldMap from "./ExtensionDynamicFieldMap";
import {IExtensionOverrideSetting, IStorefrontSettings} from "../../models/Storefront";
import DataField from "../Common/DataField";
import TokenAutosuggestField from "../Common/TokenAutosuggestField";
import VendorSelectionDropdown from '../Vendors/VendorSelectionDropdown';
import {ISavedSearch} from "../../models/Search";
import {ITokenSuggestion, useOpportunityTokens} from "../Storefront/useOpportunityTokens";
import Setting from '../Common/Setting';
import ConditionalSetting from '../Common/ConditionalSetting';
import AccordionBox from '../Common/AccordionBox';
import {useHistory} from 'react-router-dom';

interface IExtensionInstallFormProps {
    request: IExtensionInstallRequest;
    onChange: (request: IExtensionInstallRequest) => void;
    extension: IExtension;
    isNew?: boolean;
    validationResponse?: IValidateExtensionResponse;
    saveError?: string;
    installedId?: string;
    /**
     * The install's last credential check passed, so its connection fields are the one thing nobody
     * should be touching: they collapse into a labeled accordion and the day-to-day settings keep the
     * space. Leave unset for new installs and never-validated ones — those are still being set up.
     */
    credentialsHealthy?: boolean;
}

const TargetCatalogExtensions = [EXTENSION_TYPE_IMPORT, EXTENSION_TYPE_REALTIME, EXTENSION_TYPE_WEBHOOK, EXTENSION_TYPE_DIRECTIMPORT];
const VendorExtension = [EXTENSION_TYPE_REALTIME, EXTENSION_TYPE_EXPORT, EXTENSION_TYPE_IMPORT, EXTENSION_TYPE_PROCUREMENT];
const ScheduleExtensions = [EXTENSION_TYPE_IMPORT, EXTENSION_TYPE_PROCUREMENT];
const CreateProductsExtension = [EXTENSION_TYPE_IMPORT];
const defaultSchedule: ISchedule = {days: [0, 1, 2, 3, 4, 5, 6], hour: 1, minute: 0};

/**
 * What Create Products actually gates, in the operator's terms. Written from the import path itself
 * (CsvParser): the flag is only consulted for a row that matched nothing already in the catalog and had no
 * possible manufacturer-part match, so it is genuinely about ADDING records, not about updating them.
 */
const CREATE_PRODUCTS_TOOLTIP =
    "When a row in this feed matches nothing in your catalog, and no manufacturer part number match is " +
    "found either, a new product is created from it: name, description, manufacturer, category, " +
    "dimensions, and this vendor's part number, cost and availability.\n\n" +
    "Leave it off and the import only touches products you already carry. It updates them and adds this " +
    "vendor's pricing and stock, and rows it cannot match are ignored. Turning it on can grow the " +
    "catalog substantially, since a distributor feed usually lists far more than you stock.";

/**
 * Header annotation for the collapsed credential box on a validated integration: the same green check
 * the health widget uses for healthy, so "verified" reads identically everywhere it appears.
 */
export const CredentialsVerifiedBadge: FC = () => (
    // verticalAlign middle: an inline-flex span takes its baseline from its first item (the icon), which
    // would seat the text a few pixels off the title's baseline; middle-aligning the whole badge against
    // the line keeps it level with the title text.
    <span style={{marginLeft: '0.75em', color: '#107c10', display: 'inline-flex', alignItems: 'center',
        gap: 4, fontWeight: 400, verticalAlign: 'middle'}}>
        <Icon iconName='SkypeCircleCheck' style={{lineHeight: 1}} />
        Credentials verified
    </span>
);

/**
 * The counterpart to CredentialsVerifiedBadge, for a capability whose own check failed. Sits on that
 * capability rather than on the folio, because with credentials held per capability the folio has no one
 * answer and the fix is behind this particular heading.
 */
export const CredentialsFailedBadge: FC = () => (
    <span style={{marginLeft: '0.75em', color: '#a4262c', display: 'inline-flex', alignItems: 'center',
        gap: 4, fontWeight: 400, verticalAlign: 'middle'}}>
        <Icon iconName='StatusErrorFull' style={{lineHeight: 1}} />
        Credentials failed
    </span>
);

/**
 * Blocking notice for settings that ride on a folio whose last credential check failed. Rendered in
 * place of the settings form: those fields would save against — and pull their options from — a
 * connection that is known broken, so the one useful action is fixing the credentials.
 */
export const FolioCredentialsFailingBar: FC<{folio?: IInstalledExtensionFolio}> = ({folio}) => {
    const history = useHistory();
    return (
        <MessageBar messageBarType={MessageBarType.severeWarning}>
            The last credential check for {folio?.name ?? 'this integration'} failed
            {folio?.lastValidationMessage ? `: ${folio.lastValidationMessage}` : '.'}
            {' '}These settings and their option lookups are paused until a check passes.{' '}
            {folio?.id && <Link onClick={() =>
                history.push(`/settings/extensions?panel=extensionFolio&instance=${encodeURIComponent(folio.id!)}&panelTab=edit`)}>
                Fix credentials
            </Link>}
        </MessageBar>
    );
};

/** Header annotation for a settings box whose required fields are not all filled in yet. */
export const MissingRequiredSettingsBadge: FC<{count: number}> = ({count}) => (
    count > 0 ?
        <span style={{marginLeft: '0.5em', color: '#a4262c', display: 'inline-flex', alignItems: 'center',
            gap: 4, fontWeight: 400, verticalAlign: 'middle'}}>
            <Icon iconName='Warning' style={{lineHeight: 1}} />
            {count} required setting{count === 1 ? '' : 's'} missing
        </span> : <></>
);

const ExtensionInstallForm: FC<IExtensionInstallFormProps> = (props) => {
    const [error, setError] = useState<string | undefined>(undefined);
    const [connectionSettingsExpanded, setConnectionSettingsExpanded] = useState<boolean>(false);
    // Required, actually-rendered connection fields with no value yet — what the collapsed box's header
    // reports so a gap is visible without expanding it. Same renderability rule as the folio panel's
    // per-sub-extension badges: run-time fields never count as unanswered settings.
    const missingRequiredConnectionSettings = props.extension.additionalData.filter(f =>
        isRenderableStaticField(f) && !f.optional &&
        (props.request.additionalData[f.field] == undefined || props.request.additionalData[f.field] === '')).length;
    
    const [requiredFields, setRequiredFields] = useState<string[]>([]);
    const [requiredFieldsHaveValues, setRequiredFieldsHaveValues] = useState<boolean>();
    useEffect(() => {
        const required : string[] = []
        props.extension.additionalData.forEach((v) => {if(!v.optional){ required.push(v.field)}})
        
        setRequiredFields(required);
    }, [props.extension.additionalData]);

    useEffect(() => {
        setRequiredFieldsHaveValues(true)
        
        requiredFields.forEach((v) => {
            if(!(v in props.request.additionalData) || props.request.additionalData[v] === ''){
                setRequiredFieldsHaveValues(false)
                return;
            }
        })
    }, [requiredFields, props.request.additionalData]);
    
    
    //TODO: Mapping table
    return (
        <Stack tokens={{childrenGap: '1em'}}>
            {error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
            {props.validationResponse && !props.validationResponse.success &&
                <MessageBar messageBarType={MessageBarType.error}>
                    {props.validationResponse.message
                        || props.validationResponse.error
                        || props.validationResponse.validationMessages?.join(', ')
                        || 'Validation failed. Please check the extension settings and try again.'}
                </MessageBar>}
            {props.validationResponse && props.validationResponse.success && <MessageBar
                messageBarType={MessageBarType.success}>{props.validationResponse.message ?? 'Extension settings validated'}</MessageBar>}
            {props.saveError && <MessageBar messageBarType={MessageBarType.error}>{props.saveError}</MessageBar>}
            <Setting id='extensions.configuration.extensionLevel'>
            <Dropdown
                options={[{key: 'organization', text: 'Organization'}, {key: 'user', text: 'User'}]}
                label="Extension Level"
                selectedKey={props.request.extensionLevel}
                onChange={(e, d) => props.onChange({...props.request, extensionLevel: d?.key?.toString() ?? ''})}
                disabled={!props.isNew}
            />
            </Setting>
            <Setting id='extensions.configuration.nickname'>
            <TextField
                label="Nickname"
                type="text"
                onChange={(e, d) => props.onChange({...props.request, extensionNickname: d ?? ''})}
                value={props.request.extensionNickname}
            />
            </Setting>
            {props.credentialsHealthy ?
                <AccordionBox title='Connection Settings' boldTitle
                    // A gap in the required fields outranks the verified check: it is the actionable state,
                    // and the credentials being good is already implied by the box being collapsed at all.
                    secondaryTitle={missingRequiredConnectionSettings > 0
                        ? <MissingRequiredSettingsBadge count={missingRequiredConnectionSettings} />
                        : <CredentialsVerifiedBadge />}
                    isExpanded={connectionSettingsExpanded}
                    onToggle={(expanded) => setConnectionSettingsExpanded(expanded)}>
                    <Stack tokens={{childrenGap: '1em'}}>
                        <ExtensionStaticDataForm
                            staticData={props.request.additionalData}
                            onChange={(data) => props.onChange({...props.request, additionalData: data})}
                            additionalFields={props.extension.additionalData}
                            extensionId={props.extension.id}
                        />
                    </Stack>
                </AccordionBox>
                : <ExtensionStaticDataForm
                    staticData={props.request.additionalData}
                    onChange={(data) => props.onChange({...props.request, additionalData: data})}
                    additionalFields={props.extension.additionalData}
                    extensionId={props.extension.id}
                />}
            <ConditionalSetting when={TargetCatalogExtensions.includes(props.extension.category)} id='extensions.configuration.targetCatalog'>
                <Setting id='extensions.configuration.targetCatalog'>
 <ExtensionCatalogSelection
                label="Target Catalog"
                catalog={props.request.catalog}
                onChange={(catalog) => props.onChange({...props.request, catalog: catalog})}
            />
 </Setting>
            </ConditionalSetting>
            <ConditionalSetting when={VendorExtension.includes(props.extension.category)} id='extensions.configuration.vendor'>
                <Setting id='extensions.configuration.vendor'>
 <VendorSelectionDropdown
                selectedVendor={props.request.vendor}
                onChange={(vendor) => props.onChange({...props.request, vendor: vendor})}
                allowCustom={true}
                />
 </Setting>
            </ConditionalSetting>
            {props.extension.hasSampleLoad && <ExtensionMappingForm
                mappings={props.request.mappings ?? []}
                onChange={(mappings, vpnIsMfp) => props.onChange({...props.request, mappings: mappings, vpnIsMfp: vpnIsMfp})}
                vpnIsMfp={props.request.vpnIsMfp ?? false}
                staticData={props.request.additionalData}
                onError={(message) => setError(message)}
                id={props.extension.id}
                hasRequiredFields={requiredFieldsHaveValues}
                installedId={props.installedId}
            />}
            <ConditionalSetting when={CreateProductsExtension.includes(props.extension.category)} id='extensions.configuration.createProducts'>
                <Setting id='extensions.configuration.createProducts'>
 <Checkbox
                label="Create Products"
                checked={props.request.createProducts}
                onChange={(ev, checked) => props.onChange({...props.request, createProducts: checked})}
            />
 </Setting>
            </ConditionalSetting>
            {ScheduleExtensions.includes(props.extension.category) && <Scheduler
                schedule={props.request.schedule != null ? ConvertCronToSchedule(props.request.schedule) : defaultSchedule}
                onChange={(s) => props.onChange({
                    ...props.request,
                    schedule: ConvertScheduleToCron(s ?? defaultSchedule)
                })}
            />}
        </Stack>
    );
}

interface ISubExtensionInstallFormProps {
    subExtension: string;
    folio?: IInstalledExtensionFolio;
    label?: string;
    /**
     * Open showing only the settings that still need a value, with the rest behind a link.
     *
     * For a capability the operator has not finished: the point of expanding it for them is to put the
     * unfinished part in front of them, and a full form of mostly-filled fields buries the two that are
     * not. Only a starting state — the link switches to the full form and stays there.
     */
    startOutstandingOnly?: boolean;
    /**
     * Fired when the last outstanding setting is filled in while the form is showing only its band.
     *
     * The box was opened because something was missing, and it is not any more, so the caller is told to
     * put it away. Without this the form falls back to the full field list at that moment, which is the
     * opposite of what the operator just earned: they finish a two-field band and are handed the twenty
     * fields they were spared. Only fires from the collapsed state — a form they expanded themselves is
     * one they are reading, and closing it under them would be the panel arguing with them.
     */
    onOutstandingCleared?: () => void;
    onUpdateStart?: () => void;
    onUpdateStop?: () => void;
    onSetError?: (error?: string) => void;
}


export const SubExtensionInstallForm: FC<ISubExtensionInstallFormProps> = (props) => {
    const [folioUpdater] = usePutExtensionFolioMutation()
    const updateExtensionFolio = (data: { [p: string]: string }) => {
        const existingFolio = cloneDeep(props.folio)
        if (existingFolio?.subExtensionInstalledFields && props.subExtension && existingFolio.sourceExtensionFolio?.id) {
            props?.onUpdateStart?.()
            existingFolio.subExtensionInstalledFields[props.subExtension] = data
            folioUpdater({extensionId: existingFolio.sourceExtensionFolio.id, request: existingFolio}).then(() => {
                props?.onUpdateStop?.()
            })
        }
    }
    const [folioCompositeId, setFolioCompositeId] = useState<string | undefined>(undefined);
    useEffect(() => {
        let error = ''
        let errorCount = 0;
        props.folio?.sourceExtensionFolio?.subExtensions?.[props.subExtension]?.additionalData?.forEach((field) => {
            const checkValue = props.folio?.subExtensionInstalledFields?.[props.subExtension]?.[field.field]
            if(!field.optional && (checkValue == undefined || checkValue == '')) {
                if(errorCount <3) {
                    error += `Value required for ${field.description}. `
                }
                errorCount++
            }

        })
        if(errorCount > 3){
            error += ` (${errorCount-3} more validation errors not shown)`
        }
        props?.onSetError?.(error)
    }, [props.folio?.subExtensionInstalledFields, props.folio?.sourceExtensionFolio?.subExtensions?.[props.subExtension]?.additionalData])
    useEffect(() => {
        if (props.subExtension && props.subExtension != '' && props.folio?.sourceExtensionFolio?.id) {
            setFolioCompositeId(`folio_${props.folio.sourceExtensionFolio.id}_${props.subExtension}`)
        }
    }, [props.subExtension, props.folio]);
    const allFields = props.folio?.sourceExtensionFolio?.subExtensions?.[props.subExtension]?.additionalData ?? [];
    const values = props.folio?.subExtensionInstalledFields?.[props.subExtension] ?? {};
    const outstandingCount = allFields.filter(field =>
        isRenderableStaticField(field) && !field.optional && !(values[field.field] ?? '').trim()).length;
    const renderableCount = allFields.filter(isRenderableStaticField).length;

    // Tracks only the operator asking for the full form — NOT the starting state. Latching the prop into
    // useState looked equivalent and was not: this box mounts before the panel has worked out which
    // capability to open, so the initial value was always false and the band never appeared. Reading the
    // prop live means a later decision still lands, while the click below stays sticky.
    const [showAllSettings, setShowAllSettings] = useState<boolean>(false);

    /**
     * Whether this box is showing only what needs input.
     *
     * State, decided once, and NOT derived from the live count of empty fields. Derived, it flipped on
     * every edit: filling the last field dropped the count to zero, the condition went false, and the rest
     * of the form appeared the instant the operator clicked away — the thing the band exists to avoid, and
     * the same jump in reverse when a field was cleared again.
     *
     * Two things end it, both deliberate acts: asking for the full form, or a passing Confirm.
     */
    const [collapsed, setCollapsed] = useState<boolean>(false);
    const collapseSeeded = useRef(false);
    useEffect(() => {
        if (collapseSeeded.current) return;
        // The panel works out which capability to open after this mounts, so the prop arrives late.
        if (!props.startOutstandingOnly || allFields.length === 0) return;
        collapseSeeded.current = true;
        // Nothing outstanding, or everything outstanding: either way there is no subset worth showing.
        setCollapsed(outstandingCount > 0 && outstandingCount < renderableCount);
    }, [props.startOutstandingOnly, allFields.length, outstandingCount, renderableCount]);

    const collapsedToOutstanding = collapsed && !showAllSettings;

    const [validateInstalled] = useValidateExtensionFolioMutation();

    /**
     * Checks the capability's credentials as they now stand, and reports whether the band can close.
     *
     * The band used to clear itself as fields were filled and the box closed on the last one, which said
     * "done" on the strength of the fields being non-empty. A value being present is not the same as a
     * value being right, and for a credential it is the weaker of the two by a distance — so the confirm
     * asks the connector, and only a pass puts the settings away.
     */
    const confirmOutstanding = async (): Promise<boolean> => {
        const source = props.folio?.sourceExtensionFolio?.id;
        if (!source || !props.folio?.id) return true;
        props.onSetError?.(undefined);
        const result = await validateInstalled({
            id: source,
            request: {
                additionalData: values,
                folioId: props.folio.id,
                // This capability alone: it has its own credentials and its own validator, and a check
                // covering the whole folio would report on the ones that were already working.
                validationActions: [props.subExtension],
            },
        });
        if (IsMutationSuccess<IValidateExtensionResponse>(result) && result.data?.success) {
            // Proven, so there is nothing left to hold back: if this box is reopened it shows everything.
            setCollapsed(false);
            props.onOutstandingCleared?.();
            return true;
        }
        const message = IsMutationSuccess<IValidateExtensionResponse>(result)
            ? result.data?.message
            : undefined;
        props.onSetError?.(message || 'Those settings could not be verified. Check them and try again.');
        return false;
    };

    return <>
            {(props?.folio?.id != undefined && folioCompositeId) ?
                <Stack tokens={{childrenGap: '1em'}}>
                    {props.label && props.folio?.subExtensionInstalledFields?.[props.subExtension] && <h3 style={{marginTop: '2em', marginBottom: 0}}>{props.label}</h3>}
                    <ExtensionStaticDataForm
                        showTopicGroups={true}
                        staticData={values}
                        onChange={updateExtensionFolio}
                        additionalFields={allFields}
                        extensionId={folioCompositeId ?? ''}
                        updateOnDisengage
                        // Anything still needing a value is lifted above the labelled sections. Nothing is
                        // reordered: each section keeps its own contents and its own place.
                        pinOutstanding
                        outstandingOnly={collapsedToOutstanding}
                        onConfirmOutstanding={confirmOutstanding}
                    />
                    {/* Anything the caller hangs off this capability — the import schedule, on the folios
                        where exactly one capability owns it. Behind the same collapse as the settings: a
                        box opened to show two missing credentials should show two missing credentials, and
                        a schedule sitting under them is one more thing to read past. */}
                    {!collapsedToOutstanding && props.children}
                    {collapsedToOutstanding && <Link onClick={() => setShowAllSettings(true)}>
                        {`Show all ${renderableCount} settings`}
                    </Link>}
                </Stack> : <></>
            }
    </>
}

interface IStorefrontSubExtensionOverrideFormProps {
    extensionId: string;
    settings: IStorefrontSettings;
    onChange?: (settings: IStorefrontSettings) => void;
    onLoading?: (isLoading: boolean) => void;
}

// Builds the initial field values for an override form (storefront opportunity / quote request / saved-search
// export): a saved override wins; otherwise the folio's installed value is inherited. A dynamicFieldMap is special
// — its mappings are only valid for the parent selection (e.g. Opportunity Type) they were built against. When the
// override's effective parent differs from the folio's, inheriting the folio map would surface fields that don't
// exist for the override's parent ("This field is no longer available on the form…"), so we start from a clear map
// instead. A parent that matches (or a map the user already overrode) is left untouched.
const buildEffectiveOverrideData = (
    sourceFields: IAdditionalData[],
    folioFields: { [p: string]: string } | undefined,
    overrides: IExtensionOverrideSetting[] | undefined
): { [p: string]: string } => {
    const working: { [p: string]: string } = {};
    sourceFields.forEach((field) => {
        const override = overrides?.find((s) => s.name == field.field);
        if (override) {
            working[field.field] = override.value ?? '';
            return;
        }
        let inherited = folioFields?.[field.field] ?? (field.fieldType === 'dynamicFieldMap' ? '' : field.description);
        if (field.fieldType === 'dynamicFieldMap' && field.parentField) {
            const folioParent = folioFields?.[field.parentField];
            const parentOverride = overrides?.find((s) => s.name == field.parentField);
            const effectiveParent = parentOverride ? parentOverride.value : folioParent;
            if ((effectiveParent ?? '') !== (folioParent ?? '')) {
                inherited = '';
            }
        }
        working[field.field] = inherited ?? '';
    });
    return working;
};

export const StorefrontSubExtensionOverrideForm: FC<IStorefrontSubExtensionOverrideFormProps> = (props) => {
    const [effectiveStaticData, setEffectiveStaticData] = useState<{ [p: string]: string }>()
    const [folioId, setFolioId] = useState<string>();
    const [subExtensionId, setSubExtensionId]  = useState<string>();
    const extensionFolioApi = useGetExtensionFolioQuery(folioId ?? '', {skip: (!folioId || folioId == '')})
    const [currentFolio, setCurrentFolio] = useState<IInstalledExtensionFolio | undefined>();
    // Order-context tokens (order-processor + question tokens) for the field-level autosuggest.
    const {suggestions: tokenSuggestions} = useOpportunityTokens(props.settings, 'order');

    useEffect(() => {
        if(extensionFolioApi.data) {
            setCurrentFolio((_) => extensionFolioApi.data);
        }
    }, [extensionFolioApi.data]);


    useEffect(() => {
        const folioIdSplits = props.extensionId?.split('_');
        if(!folioIdSplits || folioIdSplits.length != 3) {
            return;
        }
        setFolioId(folioIdSplits[1]);
        setSubExtensionId(folioIdSplits[2]);
    }, [props.extensionId]);

    useEffect(() => {
        if(subExtensionId && currentFolio) {
            const fields = currentFolio?.subExtensionInstalledFields?.[subExtensionId]
            const sourceFields = currentFolio?.sourceExtensionFolio?.subExtensions?.[subExtensionId]?.additionalData
            setEffectiveStaticData(buildEffectiveOverrideData(sourceFields ?? [], fields, props.settings?.opportunitySettings))
        }
    }, [props.settings, currentFolio])
    const updateStorefrontOpportunitySettings = (data: { [p: string]: string }) => {
        const keys = Object.keys(data)
        const opportunitySettings: IExtensionOverrideSetting[] = keys.map((key) => {
            return {name: key, value: data[key]}
        })
        if (props.onChange) {
            props.onChange({...props.settings, opportunitySettings: opportunitySettings})
        }
    }

    // These override fields save against — and fetch their options from — the folio's connection, so a
    // folio whose credentials are known bad gets the blocking notice instead of the form (AC-1067).
    const credentialsFailing = currentFolio?.lastValidationSuccess === false;

    return (<>
            <Stack tokens={{childrenGap: '1em'}}>
                {credentialsFailing ? <FolioCredentialsFailingBar folio={currentFolio} /> :
                extensionFolioApi.isFetching || extensionFolioApi.isLoading || !effectiveStaticData ? <Spinner label={'Loading Opportunity Extension Fields...'} size={SpinnerSize.large}/>:
                <ExtensionStaticDataForm
                    showTopicGroups={true}
                    staticData={effectiveStaticData}
                    onChange={updateStorefrontOpportunitySettings}
                    additionalFields={subExtensionId ? currentFolio?.sourceExtensionFolio?.subExtensions?.[subExtensionId]?.additionalData ?? [] : []}
                    folioCompositeId={props.extensionId}
                    extensionId={props.extensionId ?? ''}
                    updateOnDisengage
                    onLoading={props.onLoading}
                    tokenSuggestions={tokenSuggestions}
                />
                }
            </Stack>
        </>
    );
}

interface ISavedSearchSubExtensionOverrideFormProps {
    extensionId: string;
    savedSearch: Partial<ISavedSearch>;
    onChange?: (settings: Partial<ISavedSearch>) => void;
    onLoading?: (isLoading: boolean) => void;
}
export const SavedSearchExportSubExtensionOverrideForm: FC<ISavedSearchSubExtensionOverrideFormProps> = (props) => {
    const [effectiveStaticData, setEffectiveStaticData] = useState<{ [p: string]: string }>()
    const [folioId, setFolioId] = useState<string>();
    const [subExtensionId, setSubExtensionId]  = useState<string>();
    const extensionFolioApi = useGetExtensionFolioQuery(folioId ?? '', {skip: (!folioId || folioId == '')})
    const [currentFolio, setCurrentFolio] = useState<IInstalledExtensionFolio | undefined>();

    useEffect(() => {
        if(extensionFolioApi.data) {
            setCurrentFolio((_) => extensionFolioApi.data);
        }
    }, [extensionFolioApi.data]);


    useEffect(() => {
        const folioIdSplits = props.extensionId?.split('_');
        if(!folioIdSplits || folioIdSplits.length != 3) {
            return;
        }
        setFolioId(folioIdSplits[1]);
        setSubExtensionId(folioIdSplits[2]);
    }, [props.extensionId]);

    useEffect(() => {
        if(subExtensionId && currentFolio) {
            const fields = currentFolio?.subExtensionInstalledFields?.[subExtensionId]
            const sourceFields = currentFolio?.sourceExtensionFolio?.subExtensions?.[subExtensionId]?.additionalData
            setEffectiveStaticData(buildEffectiveOverrideData(sourceFields ?? [], fields, props.savedSearch?.exportExtensionSettings))
        }
    }, [props.savedSearch, currentFolio])
    const updateSavedSearchExportExtensionSettings = (data: { [p: string]: string }) => {
        const keys = Object.keys(data)
        const exportExtensionSettings: IExtensionOverrideSetting[] = keys.map((key) => {
            return {name: key, value: data[key]}
        })
        if (props.onChange) {
            props.onChange({...props.savedSearch, exportExtensionSettings: exportExtensionSettings})
        }
    }

    // Same gate as the storefront opportunity forms: a folio whose credentials are known bad gets the
    // blocking notice instead of fields that could only save against — or fetch options from — a broken
    // connection.
    const credentialsFailing = currentFolio?.lastValidationSuccess === false;

    return (<>
            <Stack tokens={{childrenGap: '1em'}}>
                {credentialsFailing ? <FolioCredentialsFailingBar folio={currentFolio} /> :
                extensionFolioApi.isFetching || extensionFolioApi.isLoading ? <Spinner label={'Loading Extension Fields...'} size={SpinnerSize.large}/>:
                    <ExtensionStaticDataForm
                        showTopicGroups={true}
                        staticData={effectiveStaticData ?? {}}
                        onChange={updateSavedSearchExportExtensionSettings}
                        additionalFields={subExtensionId ? currentFolio?.sourceExtensionFolio?.subExtensions?.[subExtensionId]?.additionalData ?? [] : []}
                        folioCompositeId={props.extensionId}
                        extensionId={props.extensionId ?? ''}
                        updateOnDisengage
                        onLoading={props.onLoading}
                    />
                }
            </Stack>
        </>
    );
}

export const StorefrontSubExtensionQuoteRequestOpportunityOverrideForm: FC<IStorefrontSubExtensionOverrideFormProps> = (props) => {
    const [effectiveStaticData, setEffectiveStaticData] = useState<{ [p: string]: string }>()
    const [folioId, setFolioId] = useState<string>();
    const [subExtensionId, setSubExtensionId]  = useState<string>();
    const extensionFolioApi = useGetExtensionFolioQuery(folioId ?? '', {skip: (!folioId || folioId == '')})
    const [currentFolio, setCurrentFolio] = useState<IInstalledExtensionFolio | undefined>();
    // Quote-request-context tokens (notification + quote_request_* question tokens) for the field-level autosuggest.
    const {suggestions: tokenSuggestions} = useOpportunityTokens(props.settings, 'quoteRequest');

    useEffect(() => {
        if(extensionFolioApi.data) {
            setCurrentFolio((_) => extensionFolioApi.data);
        }
    }, [extensionFolioApi.data]);


    useEffect(() => {
        const folioIdSplits = props.extensionId?.split('_');
        if(!folioIdSplits || folioIdSplits.length != 3) {
            return;
        }
        setFolioId(folioIdSplits[1]);
        setSubExtensionId(folioIdSplits[2]);
    }, [props.extensionId]);

    useEffect(() => {
        if(subExtensionId && currentFolio) {
            const fields = currentFolio?.subExtensionInstalledFields?.[subExtensionId]
            const sourceFields = currentFolio?.sourceExtensionFolio?.subExtensions?.[subExtensionId]?.additionalData
            setEffectiveStaticData(buildEffectiveOverrideData(sourceFields ?? [], fields, props.settings?.quoteRequestOpportunitySettings))
        }
    }, [props.settings, currentFolio])
    const updateStorefrontOpportunitySettings = (data: { [p: string]: string }) => {
        const keys = Object.keys(data)
        const opportunitySettings: IExtensionOverrideSetting[] = keys.map((key) => {
            return {name: key, value: data[key]}
        })
        if (props.onChange) {
            props.onChange({...props.settings, quoteRequestOpportunitySettings: opportunitySettings})
        }
    }

    // Same gate as the order-opportunity form: a folio whose credentials are known bad gets the blocking
    // notice instead of fields that could only save against — or fetch options from — a broken connection.
    const credentialsFailing = currentFolio?.lastValidationSuccess === false;

    return (<>
            <Stack tokens={{childrenGap: '1em'}}>
                {credentialsFailing ? <FolioCredentialsFailingBar folio={currentFolio} /> :
                extensionFolioApi.isFetching || extensionFolioApi.isLoading || !effectiveStaticData ? <Spinner label={'Loading Opportunity Extension Fields...'} size={SpinnerSize.large}/>:
                    <ExtensionStaticDataForm
                        showTopicGroups={true}
                        staticData={effectiveStaticData}
                        onChange={updateStorefrontOpportunitySettings}
                        additionalFields={subExtensionId ? currentFolio?.sourceExtensionFolio?.subExtensions?.[subExtensionId]?.additionalData ?? [] : []}
                        folioCompositeId={props.extensionId}
                        extensionId={props.extensionId ?? ''}
                        updateOnDisengage
                        onLoading={props.onLoading}
                        tokenSuggestions={tokenSuggestions}
                    />
                }
            </Stack>
        </>
    );
}

interface IExtensionFolioInstallFormProps {
    folio?: IInstalledExtensionFolio
    /**
     * Suppress the import schedule here because the caller is rendering it somewhere better — under the
     * one capability that runs on it. Ignored when several capabilities share the schedule, since then it
     * genuinely belongs to the folio.
     */
    hideSchedule?: boolean
    /**
     * Display names of the capabilities the folio's single schedule drives. Shown as subtext so a shared
     * schedule says what it affects rather than leaving the reader to assume it is only theirs.
     */
    scheduleAppliesTo?: string[]
    onSetError?: (error?: string) => void
    onValidateComplete?: (response: IValidateExtensionResponse) => void;
    onValidateStart?: () => void;
    onUpdateStart?: () => void;
    onUpdateStop?: () => void;
    /**
     * Validate-first mode for a folio whose last credential check failed: nothing is saved and no option
     * lookups run. Credential field edits stay possible but only accumulate locally (reported through
     * onBlockedFieldEdit so the caller can validate them); the other settings controls are disabled —
     * changing a schedule against a connection that is known broken only manufactures false progress.
     */
    blockSaves?: boolean;
    /**
     * Validate-first for the credential fields only, used when the credentials are known GOOD: edits to
     * the shared fields accumulate locally (via onBlockedFieldEdit) instead of saving, so working
     * credentials can never be silently overwritten with unproven ones — a validation pass is what
     * applies them. Everything else on the form saves and behaves normally.
     */
    validateFirstCredentials?: boolean;
    /** The complete shared-field values as edited while either validate-first mode is on (fires per edit). */
    onBlockedFieldEdit?: (fields: { [key: string]: string }) => void;
}

/**
 * The folio's import schedule, as its own control so it can be rendered where it makes sense.
 *
 * The schedule is ONE cron for the whole folio and every scheduled capability runs on it — it is not the
 * property of any one of them. That is why the caller decides where it goes: under the single capability
 * that uses it when there is only one, and in the folio's own settings when several share it, where the
 * label says so.
 */
export const FolioScheduleSetting: FC<{
    folio?: IInstalledExtensionFolio,
    heading: string,
    description?: string,
    disabled?: boolean
}> = ({folio, heading, description, disabled}) => {
    const [folioUpdater] = usePutExtensionFolioMutation();
    const extensionId = folio?.sourceExtensionFolio?.id;
    if (!folio || !extensionId) return <></>;
    return (<>
        <h3 style={{marginBottom: 0, marginTop: '2em'}}>{heading}</h3>
        {description && <Text variant='small' style={{color: '#605e5c'}}>{description}</Text>}
        <Scheduler
            schedule={folio.schedule != null ? ConvertCronToSchedule(folio.schedule) : defaultSchedule}
            onChange={(s) => {
                if (disabled) return;
                const updated = cloneDeep(folio);
                updated.schedule = ConvertScheduleToCron(s ?? defaultSchedule);
                folioUpdater({extensionId, request: updated});
            }}
        />
    </>);
};

export const ExtensionFolioInstallForm: FC<IExtensionFolioInstallFormProps> = (props) => {
    const [validateInstalled] = useValidateExtensionFolioMutation();
    const [folioUpdater] = usePutExtensionFolioMutation();

    const [currentFolio, setCurrentFolio] = useState<IInstalledExtensionFolio | undefined>(props.folio);
    // Soft-locked identity settings (extension level / target catalog / vendor): set during connection,
    // rarely changed after, and quietly re-pointing them re-points what the integration writes to. They
    // render read-only with a lock; unlocking asks for confirmation and lasts only while the form is open.
    const [unlockedSettings, setUnlockedSettings] = useState<string[]>([]);
    const [confirmUnlockFor, setConfirmUnlockFor] = useState<{key: string, label: string} | undefined>(undefined);
    const softLocked = (key: string, hasValue: boolean) => hasValue && !unlockedSettings.includes(key);
    const lockButton = (key: string, label: string, hasValue: boolean) => (
        hasValue ? <IconButton
            iconProps={{iconName: unlockedSettings.includes(key) ? 'Unlock' : 'Lock'}}
            title={unlockedSettings.includes(key) ? `${label} is unlocked — click to lock it again` : `Unlock to change ${label}`}
            ariaLabel={unlockedSettings.includes(key) ? `Lock ${label}` : `Unlock ${label}`}
            styles={{root: {marginBottom: 2}}}
            onClick={() => unlockedSettings.includes(key)
                ? setUnlockedSettings(u => u.filter(k => k !== key))
                : setConfirmUnlockFor({key, label})}
        /> : null
    );
    const [hasCatalogSubExtension, setHasCatalogSubExtension] = useState<boolean>()
    const [hasVendorSubExtension, setHasVendorSubExtension] = useState<boolean>()
    const [hasScheduleSubExtension, setHasScheduleSubExtension] = useState<boolean>()
    const [hasCreateProductsSubExtension, setHasCreateProductsSubExtension] = useState<boolean>()
    const [needsValidation, setNeedsValidation] = useState<boolean>(true)
    const [folioCompositeId, setFolioCompositeId] = useState<string>('');
   
    useEffect(() => {
        if(props.folio?.sourceExtensionFolio) {
            setFolioCompositeId(`folio_${props.folio.sourceExtensionFolio.id}_shared`)
        }
    }, [props.folio]);
    useEffect(() => {
        setCurrentFolio(props.folio)
        setHasCatalogSubExtension(false)
        setHasVendorSubExtension(false)
        if (props.folio?.sourceExtensionFolio?.subExtensions) {
            setHasScheduleSubExtension(Object.values(props.folio.sourceExtensionFolio.subExtensions).findIndex((se) => se.extensionType === EXTENSION_TYPE_IMPORT) !== -1)
        } else {
            setHasScheduleSubExtension(false)
        }
    }, [props.folio])

    useEffect(() => {
        setNeedsValidation(true)
    }, [props.folio?.commonInstalledFields]);

    useEffect(() => {
        let fieldErrorString = ''
        if (currentFolio?.sourceExtensionFolio?.sharedFields) {
            currentFolio?.sourceExtensionFolio?.sharedFields?.forEach((field) => {
                if (!field.optional && (currentFolio.commonInstalledFields?.[field.field] == undefined || currentFolio.commonInstalledFields?.[field.field] == '')) {
                    fieldErrorString += `${field.description} is required. `
                }
            })
        }
        let catalogExt = false;
        let vendorExt = false;
        let scheduleExt = false;
        let createProductsExt = false;
        if (currentFolio?.sourceExtensionFolio?.subExtensions && currentFolio?.enabledSubExtensions) {
            currentFolio.enabledSubExtensions.forEach((subExtension) => {
                const category = currentFolio.sourceExtensionFolio?.subExtensions?.[subExtension]?.extensionType;
                if (!category) return;
                if (TargetCatalogExtensions.includes(category)) {
                    catalogExt = true
                }
                if (VendorExtension.includes(category)) {
                    vendorExt = true
                }
                if (ScheduleExtensions.includes(category)) {
                    scheduleExt = true
                }
                if (CreateProductsExtension.includes(category)) {
                    createProductsExt = true
                }
            })
        }
        setHasCatalogSubExtension(catalogExt);
        setHasVendorSubExtension(vendorExt);
        setHasScheduleSubExtension(scheduleExt);
        setHasCreateProductsSubExtension(createProductsExt);
        if (props.onSetError) {
            props.onSetError(fieldErrorString)
        }

        if (fieldErrorString === '' && needsValidation) {
            validateExisting().then()
        }
    }, [currentFolio]);

    /**
     * Runs the credential check, one call per set of credentials rather than one for the folio.
     *
     * A capability that carries its own credentials — or is checked at its own endpoint — has to be
     * checked on its own: naming several in one call sends a merged field set to a single validator and
     * stamps every one of them with that one verdict, so a working FTP feed could be marked broken
     * because the API credentials beside it were wrong. Capabilities that share the folio's credentials
     * and its validator still go together in one call, which keeps a CRM at a single round trip.
     *
     * The reported outcome is the whole folio's: it passes only if every check passed, and reports the
     * first failure, because that is what the panel's validate-first mode acts on.
     */
    const validateExisting = async () => {
        if (!props.onValidateComplete) return;
        props?.onValidateStart?.()

        if (props.folio?.id == null || props.folio?.commonInstalledFields == undefined) return;
        const sourceId = props.folio.sourceExtensionFolio?.id;
        if (!sourceId) return;

        const source = props.folio.sourceExtensionFolio;
        const enabled = props.folio.enabledSubExtensions ?? [];
        const ownCredentials = (identifier: string) => {
            const declared = source?.subExtensions?.[identifier];
            return (declared?.additionalData?.length ?? 0) > 0 || !!declared?.validateUrl;
        };

        const separately = enabled.filter(ownCredentials);
        const together = enabled.filter(id => !ownCredentials(id));

        // Each own-credential capability, then whatever is left over as one call. A folio with nothing
        // enabled still gets a single check, which is the whole-folio behaviour it had before.
        const batches: (string[] | undefined)[] = separately.map(id => [id]);
        if (together.length > 0 || batches.length === 0) {
            batches.push(together.length > 0 ? together : props.folio.enabledSubExtensions);
        }

        let outcome: IValidateExtensionResponse = {success: true};
        for (const validationActions of batches) {
            const result = await validateInstalled({
                id: sourceId,
                request: {
                    additionalData: props.folio.commonInstalledFields,
                    folioId: props.folio.id,
                    validationActions
                }
            });

            if (!IsMutationSuccess<IValidateExtensionResponse>(result)) {
                outcome = {success: false, message: 'Failed to communicate with API'};
                break;
            }
            if (!result.data?.success) {
                outcome = result.data ?? {success: false};
                break;
            }
        }

        props.onValidateComplete(outcome);
        if (outcome.success) setNeedsValidation(false);
    }

    const debouncedUpdateExtensionFolio = useCallback(
        debounce((data: IInstalledExtensionFolio) => {
            if (data.sourceExtensionFolio?.id) {
                folioUpdater({extensionId: data.sourceExtensionFolio?.id, request: data})
            }
        }, 1000, {leading: false}),
        []
    );
    const updateExtensionFolio = (data: { [p: string]: string }) => {
        if (currentFolio?.id) {
            props?.onUpdateStart?.()
            setCurrentFolio((current) => ({...current, commonInstalledFields: data}))
            // Validate-first: whether the credentials are known bad (fixing them) or known good (changing
            // them), edited credential values are never persisted directly — they are handed to the caller
            // so a validation can prove them, and the save happens on the pass.
            if (props.blockSaves || props.validateFirstCredentials) props.onBlockedFieldEdit?.(data)
            else debouncedUpdateExtensionFolio({...currentFolio, commonInstalledFields: data})
            props?.onUpdateStop?.()
        }
    }
    const setExtensionLevel = (selection: string) => {
        if (props.blockSaves) return;
        const existingFolio = cloneDeep(currentFolio)
        if (existingFolio?.sourceExtensionFolio?.id) {
            existingFolio.extensionLevel = selection;

            folioUpdater({extensionId: existingFolio?.sourceExtensionFolio?.id, request: existingFolio})
        }
    }
    const setVendor = (selection?: string) => {
        setCurrentFolio((current) => ({...current, vendor: selection}));
    }
    const setCatalog = (selection?: string) => {
        if (props.blockSaves) return;
        const existingFolio = cloneDeep(currentFolio)
        if (existingFolio?.sourceExtensionFolio?.id) {
            existingFolio.targetCatalog = selection;
            folioUpdater({extensionId: existingFolio?.sourceExtensionFolio?.id, request: existingFolio})
        }
    }
    const setSchedule = (selection?: string) => {
        if (props.blockSaves) return;
        const existingFolio = cloneDeep(currentFolio)
        if (existingFolio?.sourceExtensionFolio?.id) {
            existingFolio.schedule = selection;
            folioUpdater({extensionId: existingFolio?.sourceExtensionFolio?.id, request: existingFolio})
        }
    }
    const setCreateProduct = (ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        if (props.blockSaves) return;
        const existingFolio = cloneDeep(currentFolio)
        if (existingFolio?.sourceExtensionFolio?.id) {
            existingFolio.createProducts = checked ?? false;
            folioUpdater({extensionId: existingFolio?.sourceExtensionFolio?.id, request: existingFolio})
        }
    }
    return (<>
            {/* Gated on having a folio, NOT on it having shared credentials. Extension level, target
                catalog, vendor, the schedule and Create Products are folio settings in their own right;
                requiring a shared credential set to show any of them left a folio that keeps its
                credentials per capability with an empty Common Settings heading and nothing under it. */}
            {(currentFolio?.id != undefined) ?
                <Stack tokens={{childrenGap: '1em'}}>
                    {/* While the credentials are known bad, only the credential fields render: the other
                        settings cannot save in that state, and a control that silently ignores input is
                        worse than one that is not there. */}
                    {!props.blockSaves && <Setting id='extensions.configuration.extensionLevel'>
                    <Stack horizontal verticalAlign='end' tokens={{childrenGap: 4}}>
                        <StackItem grow>
                    <Dropdown
                        options={[{key: 'organization', text: 'Organization'}, {key: 'user', text: 'User'}]}
                        label="Extension Level"
                        selectedKey={currentFolio.extensionLevel || 'organization'}
                        disabled={softLocked('extensionLevel', true)}
                        onChange={(e, d) => {
                            if (d) {
                                setExtensionLevel(d.key.toString())
                            }
                        }}
                    />
                        </StackItem>
                        {lockButton('extensionLevel', 'Extension Level', true)}
                    </Stack>
                    </Setting>}
                    <ConditionalSetting when={!!hasCatalogSubExtension && !props.blockSaves} id='extensions.configuration.targetCatalog'>
                        <Setting id='extensions.configuration.targetCatalog'>
                    <Stack horizontal verticalAlign='end' tokens={{childrenGap: 4}}>
                        <StackItem grow>
 <ExtensionCatalogSelection
                        label="Target Catalog"
                        catalog={currentFolio.targetCatalog}
                        disabled={softLocked('targetCatalog', !!currentFolio.targetCatalog)}
                        onChange={(catalog) => setCatalog(catalog)}
                    />
                        </StackItem>
                        {lockButton('targetCatalog', 'Target Catalog', !!currentFolio.targetCatalog)}
                    </Stack>
 </Setting>
                    </ConditionalSetting>
                    <ConditionalSetting when={!!hasVendorSubExtension && !props.blockSaves} id='extensions.configuration.vendor'>
                        <Setting id='extensions.configuration.vendor'>
                    <Stack horizontal verticalAlign='end' tokens={{childrenGap: 4}}>
                        <StackItem grow>
 <VendorSelectionDropdown
                        selectedVendor={currentFolio.vendor}
                        onChange={setVendor}
                        allowCustom={true}
                        disabled={softLocked('vendor', !!currentFolio.vendor)}
                        onBlur={() => { if (!props.blockSaves) debouncedUpdateExtensionFolio(currentFolio) }}
                        />
                        </StackItem>
                        {lockButton('vendor', 'Vendor', !!currentFolio.vendor)}
                    </Stack>
 </Setting>
                    </ConditionalSetting>
                    {confirmUnlockFor && <Dialog
                        hidden={false}
                        onDismiss={() => setConfirmUnlockFor(undefined)}
                        dialogContentProps={{
                            type: DialogType.normal,
                            title: `Change ${confirmUnlockFor.label}?`,
                            subText: `${confirmUnlockFor.label} was set when this integration was connected and rarely needs to change afterwards — imports and links already point at it. Unlock it only if you mean to re-point this integration.`,
                        }}>
                        <DialogFooter>
                            <PrimaryButton text='Unlock' onClick={() => {
                                setUnlockedSettings(u => [...u, confirmUnlockFor.key]);
                                setConfirmUnlockFor(undefined);
                            }} />
                            <DefaultButton text='Cancel' onClick={() => setConfirmUnlockFor(undefined)} />
                        </DialogFooter>
                    </Dialog>}
                    {/* Only where the folio actually declares shared credentials. An empty field list
                        renders an empty form, which is what made this block look broken. */}
                    {(currentFolio.sourceExtensionFolio?.sharedFields?.length ?? 0) > 0 &&
                        <ExtensionStaticDataForm
                            staticData={currentFolio.commonInstalledFields ?? {}}
                            onChange={updateExtensionFolio}
                            // Mount-time auto-selected defaults update the working values only: opening the
                            // form must not save anything (or, in validate-first mode, trigger a check).
                            onAutoChange={(data) => setCurrentFolio((current) => ({...current, commonInstalledFields: data}))}
                            additionalFields={currentFolio.sourceExtensionFolio?.sharedFields ?? []}
                            extensionId={folioCompositeId}
                            updateOnDisengage={true}
                            suppressOptionLookups={props.blockSaves}
                        />}
                    {hasScheduleSubExtension && !props.blockSaves && !props.hideSchedule && <>
                        <h3 style={{marginBottom: 0, marginTop: '2em'}}>Import Schedule</h3>
                        {/* It lives here precisely because more than one capability runs on it, so it has
                            to say which — otherwise it reads as the schedule for whatever the reader has
                            in mind. */}
                        <Text variant='small' style={{color: '#605e5c'}}>
                            {props.scheduleAppliesTo?.length
                                ? `Runs ${props.scheduleAppliesTo.join(', ')}. They share one schedule.`
                                : 'One schedule for every scheduled extension on this folio.'}
                        </Text>
                        <Scheduler
                            schedule={currentFolio.schedule != null ? ConvertCronToSchedule(currentFolio.schedule) : defaultSchedule}
                            onChange={(s) => setSchedule(ConvertScheduleToCron(s ?? defaultSchedule))}
                        />
                    </>}
                    <ConditionalSetting when={!!hasCreateProductsSubExtension && !props.blockSaves} id='extensions.configuration.createProducts'>
                    <div>
                        <Setting id='extensions.configuration.createProducts'>
                        {/* Label carries the tooltip rather than the checkbox, matching how option fields
                            document themselves elsewhere in this form. */}
                        <Checkbox
                            checked={currentFolio.createProducts}
                            disabled={props.blockSaves}
                            onChange={setCreateProduct}
                            onRenderLabel={() => (
                                <TooltipHost content={<MarkdownView markdown={CREATE_PRODUCTS_TOOLTIP} />}>
                                    <span style={{display: 'inline-flex', alignItems: 'center'}}>
                                        Create Products
                                        <Icon iconName='Info' style={{paddingLeft: '5px'}} />
                                    </span>
                                </TooltipHost>
                            )}
                            />
                        <Text variant='small' style={{color: '#605e5c', display: 'block', paddingLeft: '1.9em'}}>
                            Add products from this feed that are not in your catalog yet.
                        </Text>
                        </Setting>
                        {currentFolio.createProducts &&  <div style={{paddingTop: '1em'}}>
                            {/* Runs are started from the Activity tab, where each sub-extension can be run on its
                                own instead of everything the folio has enabled. */}
                            <MessageBar messageBarType={MessageBarType.info}>
                                To import now, use the Run button on the Activity tab.
                            </MessageBar>
                        </div>}
                    </div>
                    </ConditionalSetting>
                </Stack> : <></>
            }</>
    );
}

export interface IExtensionStaticDataFormProps {
    staticData: { [key: string]: string }
    onChange?: (data: { [key: string]: string }) => void;
    additionalFields: IAdditionalData[];
    /**
     * Lift the settings that still need a value into a band above the labelled sections.
     *
     * They are moved, not copied: a field appears in the band while it is outstanding and in its own
     * section once it has a value, so there is never a second live input bound to the same setting. That
     * also means the band empties itself as the form is filled in, which is the point of it.
     *
     * Off by default. Reordering a form people know is disruptive; this leaves every section exactly
     * where it was and only takes out what is unfinished.
     */
    pinOutstanding?: boolean;
    /**
     * Render only that band. For a form opened on the operator's behalf because it was unfinished: the
     * rest of the settings are still reachable, behind whatever control the caller offers.
     */
    outstandingOnly?: boolean;
    /**
     * Runs when the operator confirms the band, once every field in it has a value. Returning false keeps
     * the band open — this is where the caller checks the values against the connector, and a check that
     * fails is the one time the band has more to say rather than less.
     *
     * Without it the band offers no confirm step and simply empties as fields are filled.
     */
    onConfirmOutstanding?: () => Promise<boolean>;
    /** Keystrokes from the fields this form renders. Only the pinned band asks for them. */
    onLiveFieldChange?: (field: string, value: string) => void;
    extensionId: string;
    folioCompositeId?: string;
    updateOnDisengage?: boolean;
    showTopicGroups?: boolean;
    onLoading?: (isLoading: boolean) => void;
    // When provided, tokenizable text fields (field.supportsTokens) offer these as inline autosuggest. Only the
    // storefront opportunity override forms pass this; everywhere else fields render as plain inputs.
    tokenSuggestions?: ITokenSuggestion[];
    // Option dropdowns are filled by live credentialed calls to the extension. When the credentials are
    // known to be failing those calls can only error, so the form skips them (batch and per-field alike)
    // and the dropdowns say to validate credentials first.
    suppressOptionLookups?: boolean;
    // Receives mount-time auto-selections (an option field re-announcing its stored value or picking its
    // declared default). Callers whose onChange persists or validates pass a local-state-only handler here
    // so merely opening the form never counts as editing it; unset, auto-selections fall through to onChange.
    onAutoChange?: (data: { [key: string]: string }) => void;
}

// Exported so streamlined flows (e.g. the Getting Started CRM setup dialog) can render a folio's
// shared credential fields without the surrounding power-user chrome of ExtensionFolioInstallForm.
/**
 * Whether this form draws a control for the field at all.
 *
 * Not every declared field is a setting somebody configures here: `search` fields (an extension's
 * ad-hoc entity lookup, used while a job runs) and the other run-time types carry no input on a
 * configuration screen, so this form skips them.
 *
 * Exported because callers reason about these fields too, and a caller working from its own idea of
 * what renders drifts from what does. Counting a `search` field as an unanswered required setting is
 * exactly that bug: the wizard reported a setting needing input under a heading with nothing beneath it.
 */
export const isRenderableStaticField = (field: IAdditionalData): boolean =>
    field.fieldType === 'defaultedOption' || field.fieldType === 'defaultedText' ||
    field.fieldType === 'static' || field.fieldType === 'installOption' ||
    field.fieldType === 'flag' || field.fieldType === 'dynamicFieldMap';

export const ExtensionStaticDataForm: React.FC<IExtensionStaticDataFormProps> = (props) => {
    const [additionaFieldTopicGroups, setAdditionalFieldsTopicGroup] = useState<{[key: string]: IAdditionalData[]}>();
    const [topicGroups, setTopicGroups] = useState<string[]>([])
    const [hiddenTopicGroups, setHiddenTopicGroups] = useState<string[]>([]);
    const [loadingFields, setLoadingFields] = useState<string[]>([])
    const [workingId, setWorkingId] = useState<string>('');

    /**
     * The one outstanding required field that gets to explain itself.
     *
     * Declaration order, skipping anything a fieldFilter is currently hiding: a field nobody can see is
     * not the top of the list. The grouped layout renders by topic, so in that mode this is the first in
     * declaration order rather than strictly the topmost on screen -- close enough that the message lands
     * on a field the operator is looking at, and it moves down the form as each is filled in.
     */
    const hiddenByFilter = (field: IAdditionalData) =>
        field.fieldFilter?.filterType === 'hideIfNot' &&
        (!(field.fieldFilter.target in props.staticData) ||
            props.staticData[field.fieldFilter.target] !== field.fieldFilter.value);

    const firstOutstandingRequired = props.additionalFields.find(field =>
        !field.optional
        && !hiddenByFilter(field)
        && !(props.staticData[field.field] ?? '').trim()
    )?.field;
    
    useEffect(() => {
        if(props.folioCompositeId){
            setWorkingId(props.folioCompositeId)
        }
        else{
            setWorkingId(props.extensionId)
        }
    }, [props.folioCompositeId, props.extensionId]);
    
    useEffect(() => {
        if(loadingFields.length == 0) {
           props.onLoading?.(false) 
        }
        else{
            props.onLoading?.(true)
        }
    }, [loadingFields]);
    useEffect(() => {
        if (props.additionalFields) {
            const groupedFields = props.additionalFields.reduce((acc, current) => {
                const topic = current.topic ?? "General";
                if (!acc[topic]) {
                    acc[topic] = [];
                }
                if (isRenderableStaticField(current)) {
                    acc[topic].push(current);
                }
                return acc;
            }, {} as { [key: string]: IAdditionalData[] });
            setTopicGroups(Object.keys(groupedFields))
            setAdditionalFieldsTopicGroup(groupedFields);
        }
    }, [props.additionalFields]);

    // ── Batch options ─────────────────────────────────────────────────────────
    // Parentless, non-searchable option dropdowns can be fetched in a single call when the extension exposes a
    // batch URL. Searchable (typeahead) and parent-dependent fields are excluded — they keep their per-field fetch.
    // When no batch URL is configured the batch call returns null and every field falls back to today's behavior.
    const [optionsBatchApi] = useLazyGetExtensionOptionsBatchQuery();
    const [batchState, setBatchState] = useState<'idle' | 'pending' | 'done' | 'none'>('idle');
    const [batchOptions, setBatchOptions] = useState<{ [field: string]: IExtensionOption[] }>({});
    const batchCandidateFields = useMemo(
        () => new Set((props.additionalFields ?? [])
            .filter(f => f.fieldType === 'defaultedOption' && f.hasOptions && !f.searchable && !f.parentField)
            .map(f => f.field)),
        [props.additionalFields]);

    useEffect(() => {
        if (!workingId || batchState !== 'idle') return;
        if (props.suppressOptionLookups) { setBatchState('none'); return; }
        if (batchCandidateFields.size === 0) { setBatchState('none'); return; }
        setBatchState('pending');
        optionsBatchApi({
            extensionId: workingId,
            fields: Array.from(batchCandidateFields),
            additionalData: props.staticData
        }).then((res) => {
            if (!res.data) { setBatchState('none'); return; } // no batch URL — per-field fallback everywhere
            const map: { [field: string]: IExtensionOption[] } = {};
            for (const [field, response] of Object.entries(res.data)) {
                if (response?.success && response.options) map[field] = response.options;
            }
            setBatchOptions(map);
            setBatchState('done');
        }).catch(() => setBatchState('none'));
    }, [workingId, batchState, batchCandidateFields]);

    // Re-run the batch when the target extension changes (e.g. switching folios within a persisted form).
    useEffect(() => {
        setBatchState('idle');
        setBatchOptions({});
    }, [workingId]);

    // Props handed to each ExtensionDependentOption so it cooperates with the batch (wait while pending, use
    // preloaded options when present) or falls back to its own per-field fetch.
    const batchPropsFor = (field: IAdditionalData) => ({
        batchPending: batchCandidateFields.has(field.field) && batchState === 'pending',
        preloadedOptions: batchOptions[field.field],
        suppressFetch: props.suppressOptionLookups
    });

    const isDependentFieldAccessible = (field: IAdditionalData) => {
        if (!field.parentField) {
            return true;
        }
        return (props.staticData?.[field.parentField] ?? '') != ''
    }

    const getParentFieldValue = (field: IAdditionalData) => {
        if (!field.parentField) {
            return undefined;
        }
        return props.staticData?.[field.parentField]
    }

    // Mount-time auto-selections take the same subordinate-clearing shape as user edits but land on
    // onAutoChange when the caller provides one: applying a declared default to the working values is
    // wanted, persisting because the form happened to open is not.
    const applyAutoSelect = (fieldKey: string, value: string) => {
        if (props.staticData[fieldKey] == value) return
        const handler = props.onAutoChange ?? props.onChange;
        if (!handler) return
        const staticData = {...props.staticData};
        props.additionalFields.filter((f) => f.parentField === fieldKey).forEach((f) => {
            staticData[f.field] = '';
        })
        handler({...staticData, [fieldKey]: value})
    }
    const getRequiredElement = (field: IAdditionalData) => {
        if(!field.optional){
            return <div style={{display: "inline-flex", color: "rgb(164, 38, 44)", paddingLeft: "6px", paddingRight: "12px"}}>*</div>
        }
        return <></>
    }
    // A dynamicFieldMap stores a JSON blob under its own key. It has no subordinate fields to clear; if it declares
    // a parentField (e.g. the selected form/ticket type), the existing subordinate-clear logic zeroes this field's
    // value when the parent changes, and the control refetches metadata + resets.
    const renderDynamicFieldMap = (field: IAdditionalData) => (
        <ExtensionDynamicFieldMap
            key={field.field}
            field={field}
            value={props.staticData[field.field] ?? ''}
            parentFieldValue={getParentFieldValue(field)}
            extensionId={workingId}
            updateOnDisengage={props.updateOnDisengage}
            // Always offer token autosuggest inside a dynamic field map when the form supplies tokens (i.e. the
            // opportunity override context). Unlike a plain text field — gated on its own supportsTokens flag — the
            // map is a token-carrying field by design, so we don't require the flag to be set per map field.
            tokenSuggestions={props.tokenSuggestions}
            onChange={(value) => {
                if (props.staticData[field.field] == value) return
                props.onChange?.({...props.staticData, [field.field]: value})
            }}
            onLoading={(isLoading) => {
                setLoadingFields((a) => isLoading ? a.concat(field.field) : a.filter(f => f != field.field))
            }}
        />
    )
    // Sub-extension fields inside a folio are keyed by a composite `folio_<folioId>_<subExtension>`
    // rather than a catalog extension id - note SubExtensionInstallForm passes that composite as
    // `extensionId`, so both forms have to be excluded. An id built from it would resolve to an
    // instance filter matching no installed extension, i.e. a link that opens an empty picker.
    // Linking folio sub-extension fields needs its own id shape and is not supported yet.
    const isFolioField = !!props.folioCompositeId || props.extensionId?.startsWith('folio_');
    const settingIdFor = (field: string): string | undefined =>
        isFolioField || !props.extensionId ? undefined : `extension.${props.extensionId}.${field}`;

    // A field the form is currently hiding is not outstanding: nobody can fill in what they cannot see,
    // and pinning it would put an unexplainable empty box at the top of the form.
    const hiddenByFieldFilter = (field: IAdditionalData) =>
        field.fieldFilter?.filterType === 'hideIfNot' &&
        (!(field.fieldFilter.target in props.staticData) ||
            props.staticData[field.fieldFilter.target] !== field.fieldFilter.value);

    const outstandingNow = props.pinOutstanding
        ? props.additionalFields.filter(field =>
            isRenderableStaticField(field)
            && !field.optional
            && !hiddenByFieldFilter(field)
            && !(props.staticData[field.field] ?? '').trim())
        : [];

    /**
     * Which fields the band holds: decided once, when the form is opened on this capability, and then left
     * alone until it is confirmed.
     *
     * Two things this is deliberately NOT.
     *
     * It is not recomputed as fields are filled — that made a field leave the band the moment it got a
     * value, so the control the operator had just used vanished from under the cursor and the ones below
     * it jumped up.
     *
     * And it is not recomputed as fields are emptied. Clearing a box is how somebody works out what
     * belongs in it; having the setting tear itself out of the form and reappear in a band overhead is the
     * form arguing with them mid-thought. What needs input is a question asked when the panel opens, not a
     * running commentary. The band is seeded once per capability and never re-seeds — not after a confirm
     * clears it, not when a value is removed. Reopening the panel asks the question again.
     */
    const [pinnedNames, setPinnedNames] = useState<string[] | undefined>(undefined);
    const [confirming, setConfirming] = useState(false);
    /**
     * What the band's boxes read right now, which is not the same as what has been saved.
     *
     * A text field commits on blur, so waiting for staticData meant Confirm stayed disabled until the
     * operator clicked somewhere else — having typed the last value, they were looking at a finished form
     * and a dead button. This is only ever read to decide whether Confirm is available; the save still
     * happens on disengage.
     */
    const [liveValues, setLiveValues] = useState<{ [field: string]: string }>({});
    const outstandingSignature = outstandingNow.map(field => field.field).join('|');
    const seededFor = useRef<string | undefined>(undefined);
    useEffect(() => {
        if (!props.pinOutstanding) {
            seededFor.current = undefined;
            setPinnedNames(undefined);
            return;
        }
        // Keyed on the form's identity rather than a bare flag, so pointing the same mounted form at
        // another capability asks again, while every later edit to this one does not.
        if (seededFor.current === props.extensionId) return;
        // Nothing to read yet. Seeding from an empty field list would settle the answer as "nothing is
        // missing" before the fields have arrived.
        if (props.additionalFields.length === 0) return;
        seededFor.current = props.extensionId;
        setPinnedNames(outstandingSignature ? outstandingSignature.split('|') : undefined);
        // outstandingSignature is read, not depended on: it changes on every edit, and re-running then is
        // the behaviour this exists to prevent. The guard above is what decides when it is read.
    }, [props.pinOutstanding, props.extensionId, props.additionalFields.length]);

    const outstandingFields = pinnedNames
        ? props.additionalFields.filter(field => pinnedNames.includes(field.field))
        : [];
    // What the confirm waits on: every field the band holds now has something in it — as typed, not as
    // saved, so the button comes alive on the last keystroke rather than on the click that follows it.
    const bandComplete = outstandingFields.length > 0 && outstandingFields.every(
        field => ((liveValues[field.field] ?? props.staticData[field.field]) ?? '').trim().length > 0);

    const confirmBand = async () => {
        if (!props.onConfirmOutstanding) {
            setPinnedNames(undefined);
            return;
        }
        setConfirming(true);
        const passed = await props.onConfirmOutstanding();
        setConfirming(false);
        // A failed check leaves the band exactly as it is, with the values that failed still in it.
        if (passed) {
            setPinnedNames(undefined);
            setLiveValues({});
        }
    };

    const pinnedSet = new Set(outstandingFields.map(field => field.field));
    // What the sections below get: everything except what the band has taken.
    const remainingFields = pinnedSet.size > 0
        ? props.additionalFields.filter(field => !pinnedSet.has(field.field))
        : props.additionalFields;

    const outstandingBand = outstandingFields.length > 0 && (
        // Given its own surface so the band reads as an area rather than as the form's first section — it
        // is the one part of the form whose contents move as it is filled in, and it sits above headings
        // that stay put. Not an error treatment: these settings are unfinished, not wrong.
        <div style={{
            marginBottom: '1.5em',
            padding: '0.75em 1em 1em',
            border: `1px solid ${OUTSTANDING_BAND_BORDER}`,
            borderRadius: '2px',
            backgroundColor: OUTSTANDING_BAND_BACKGROUND
        }}>
            <h3 style={{marginTop: 0, marginBottom: '0.25em'}}>
                {outstandingFields.length === 1
                    ? '1 setting needs your input'
                    : `${outstandingFields.length} settings need your input`}
            </h3>
            {/* The same component, flat, over just these fields — so every field type, filter and option
                lookup behaves exactly as it does in its own section, with no second copy of the renderer
                to drift. pinOutstanding is off on this one, which is also what ends the recursion. */}
            <ExtensionStaticDataForm
                {...props}
                additionalFields={outstandingFields}
                showTopicGroups={false}
                pinOutstanding={false}
                outstandingOnly={false}
                onConfirmOutstanding={undefined}
                onLiveFieldChange={(field, value) => setLiveValues(prev => ({...prev, [field]: value}))}
            />
            {/* Deliberately not enabled until the last one is filled: this is the operator saying "these
                are right", and the check behind it costs a round trip to the connector. */}
            {props.onConfirmOutstanding && <Stack horizontal verticalAlign='center' tokens={{childrenGap: '0.75em'}}
                styles={{root: {marginTop: '0.5em'}}}>
                <PrimaryButton
                    text={confirming ? 'Checking…' : 'Confirm'}
                    disabled={!bandComplete || confirming}
                    onClick={confirmBand}
                />
                {!bandComplete && <Text variant='small' style={{color: '#605e5c'}}>
                    Fill in the settings above to confirm them.
                </Text>}
            </Stack>}
        </div>
    );

    if (props.outstandingOnly && outstandingFields.length > 0) {
        return <div>{outstandingBand}</div>;
    }

    return (<div>
    {outstandingBand}
    {!props.showTopicGroups && remainingFields.map((field) => {
            if (field.fieldFilter != null) {
                switch (field.fieldFilter.filterType) {
                    case 'hideIfNot':
                        if (!(field.fieldFilter.target in props.staticData)) return null;
                        if (props.staticData[field.fieldFilter.target] !== field.fieldFilter.value) return null;
                        break;
                }
            }
            switch (field.fieldType) {
                case 'static':
                    return (<ExtensionStaticDataField
                        key={field.field}
                        suppressRequiredMessage={field.field !== firstOutstandingRequired}
                        settingId={settingIdFor(field.field)}
                        updateOnDisengage={props.updateOnDisengage}
                        onLiveChange={(value) => props.onLiveFieldChange?.(field.field, value)}
                        tokenSuggestions={props.tokenSuggestions}
                        field={field}
                        value={props.staticData[field.field] ?? ''}
                        onChange={(value) => {
                            if (props.staticData[field.field] == value) return
                            if (props.onChange) {
                                const staticData = {...props.staticData};
                                const subOrdinateFields = props.additionalFields.filter((f) => f.parentField === field.field);
                                if (subOrdinateFields.length > 0) {
                                    subOrdinateFields.forEach((f) => {
                                        staticData[f.field] = '';
                                    })
                                }
                                props.onChange({...staticData, [field.field]: value})
                            }
                        }
                        }
                    />)
                case 'defaultedText':
                    return (<ExtensionStaticDataField
                        key={field.field}
                        suppressRequiredMessage={field.field !== firstOutstandingRequired}
                        settingId={settingIdFor(field.field)}
                        updateOnDisengage={props.updateOnDisengage}
                        onLiveChange={(value) => props.onLiveFieldChange?.(field.field, value)}
                        tokenSuggestions={props.tokenSuggestions}
                        field={field}
                        value={props.staticData[field.field] ?? ''}
                        onChange={(value) => {
                            if (props.staticData[field.field] == value) return
                            if (props.onChange) {
                                const staticData = {...props.staticData};
                                const subOrdinateFields = props.additionalFields.filter((f) => f.parentField === field.field);
                                if (subOrdinateFields.length > 0) {
                                    subOrdinateFields.forEach((f) => {
                                        staticData[f.field] = '';
                                    })
                                }
                                props.onChange({...staticData, [field.field]: value})
                            }
                        }}
                    />)
                //Largely used for dynamic fields that can be set external to the extension setup screen.
                case 'defaultedOption':
                    if (field.searchable) {
                        return (<React.Fragment key={field.field}>
                            <Label>{field.description}{getRequiredElement(field)}</Label>
                            <ExtensionFieldSearch
                                field={field}
                                initialValue={props.staticData[field.field] ?? ''}
                                onChange={(value) => {
                                    if (props.staticData[field.field] == value) return
                                    if (props.onChange) {
                                        const staticData = {...props.staticData};
                                        const subOrdinateFields = props.additionalFields.filter((f) => f.parentField === field.field);
                                        if (subOrdinateFields.length > 0) {
                                            subOrdinateFields.forEach((f) => {
                                                staticData[f.field] = '';
                                            })
                                        }
                                        props.onChange({...staticData, [field.field]: value})
                                    }
                                }
                                }
                                parentFieldValue={getParentFieldValue(field)}
                                extensionId={workingId}
                            /></React.Fragment>)
                    }

                    return (<ExtensionDependentOption
                        key={field.field}
                        parentFieldValue={getParentFieldValue(field)}
                        initialValue={props.staticData[field.field] ?? ''}
                        optionsField={field}
                        {...batchPropsFor(field)}
                        onAutoSelect={applyAutoSelect}
                        onChange={(fieldKey, value) => {
                            if (props.staticData[fieldKey] == value) return
                            if (props.onChange) {
                                const staticData = {...props.staticData};
                                const subOrdinateFields = props.additionalFields.filter((f) => f.parentField === fieldKey);
                                if (subOrdinateFields.length > 0) {
                                    subOrdinateFields.forEach((f) => {
                                        staticData[f.field] = '';
                                    })
                                }
                                props.onChange({...staticData, [field.field]: value})
                            }
                        }
                        }
                        extensionId={workingId}
                        readOnly={!isDependentFieldAccessible(field)}
                        onLoading={(value) => {
                            if(value){
                                setLoadingFields((a) => a.concat(field.field))
                            }
                            else{
                                setLoadingFields((a) => a.filter(f => f!= field.field))
                            }
                        }}
                    />)

                case 'installOption':

                    return (<ExtensionInstallOption
                        key={field.field}
                        initialValue={props.staticData[field.field] ?? ''}
                        optionsField={field}
                        onAutoSelect={applyAutoSelect}
                        onChange={(fieldKey, value) => {
                            if (props.staticData[fieldKey] == value) return
                            if (props.onChange) {
                                const staticData = {...props.staticData};
                                const subOrdinateFields = props.additionalFields.filter((f) => f.parentField === fieldKey);
                                if (subOrdinateFields.length > 0) {
                                    subOrdinateFields.forEach((f) => {
                                        staticData[f.field] = '';
                                    })
                                }
                                props.onChange({...staticData, [field.field]: value})
                            }
                        }
                        }
                        extensionId={workingId}
                    />)
                case 'flag':
                    return (
                        <div key={`extension-flag-${field.field}`}>
                            <Checkbox checked={props.staticData[field.field] == 'true'}
                                      label={field.description}
                                      onChange={(fieldKey, value) => {
                                          if (props.staticData[field.field] == value?.toString()) return
                                          if (props.onChange) {
                                              const staticData = {...props.staticData};
                                              const subOrdinateFields = props.additionalFields.filter((f) => f.parentField === fieldKey);
                                              if (subOrdinateFields.length > 0) {
                                                  subOrdinateFields.forEach((f) => {
                                                      staticData[f.field] = '';
                                                  })
                                              }
                                              props.onChange({
                                                  ...staticData,
                                                  [field.field]: value?.toString() ?? 'false'
                                              })
                                          }
                                      }
                                      }
                            />
                        </div>
                    );
                case 'dynamicFieldMap':
                    return renderDynamicFieldMap(field);
                default:
                    console.warn(`Field type ${field.fieldType} not supported in ExtensionStaticDataForm`, field);
                    return null;
            }
        })
    }
    {props.showTopicGroups && additionaFieldTopicGroups && topicGroups.map(group => {
        // Minus whatever the band above has taken, so a setting is never live in two places at once. A
        // section left with nothing is dropped rather than rendered as a bare heading.
        const groupFields = (additionaFieldTopicGroups[group] ?? [])
            .filter(field => !pinnedSet.has(field.field));
        if(groupFields.length == 0) {
            return <div></div>
        }
        return(
            <div style={{paddingTop: "10px", paddingBottom: "20px", width: "500px", display: "inline-table", paddingRight: "30px"}}><h3>{group + " "}Settings</h3>

            {groupFields.map((field) => {
                if (field.fieldFilter != null) {
                    switch (field.fieldFilter.filterType) {
                        case 'hideIfNot':
                            if (!(field.fieldFilter.target in props.staticData)) return null;
                            if (props.staticData[field.fieldFilter.target] !== field.fieldFilter.value) return null;
                            break;
                    }
                }
                switch (field.fieldType) {
                    case 'static':
                        return <ExtensionStaticDataField
                            key={field.field}
                            suppressRequiredMessage={field.field !== firstOutstandingRequired}
                            settingId={settingIdFor(field.field)}
                            updateOnDisengage={props.updateOnDisengage}
                            onLiveChange={(value) => props.onLiveFieldChange?.(field.field, value)}
                            tokenSuggestions={props.tokenSuggestions}
                            field={field}
                            value={props.staticData[field.field] ?? ''}
                            onChange={(value) => {
                                if (props.staticData[field.field] == value) return
                                if (props.onChange) {
                                    const staticData = {...props.staticData};
                                    const subOrdinateFields = props.additionalFields.filter((f) => f.parentField === field.field);
                                    if (subOrdinateFields.length > 0) {
                                        subOrdinateFields.forEach((f) => {
                                            staticData[f.field] = '';
                                        })
                                    }
                                    props.onChange({...staticData, [field.field]: value})
                                }
                            }
                            }
                        />
                    case 'defaultedText':
                        return (<ExtensionStaticDataField
                            key={field.field}
                            suppressRequiredMessage={field.field !== firstOutstandingRequired}
                            settingId={settingIdFor(field.field)}
                            updateOnDisengage={props.updateOnDisengage}
                            onLiveChange={(value) => props.onLiveFieldChange?.(field.field, value)}
                            tokenSuggestions={props.tokenSuggestions}
                            field={field}
                            value={props.staticData[field.field] ?? ''}
                            onChange={(value) => {
                                if (props.staticData[field.field] == value) return
                                if (props.onChange) {
                                    const staticData = {...props.staticData};
                                    const subOrdinateFields = props.additionalFields.filter((f) => f.parentField === field.field);
                                    if (subOrdinateFields.length > 0) {
                                        subOrdinateFields.forEach((f) => {
                                            staticData[f.field] = '';
                                        })
                                    }
                                    props.onChange({...staticData, [field.field]: value})
                                }
                            }}
                        />)
                    //Largely used for dynamic fields that can be set external to the extension setup screen.
                    case 'defaultedOption':
                        if (field.searchable) {
                            return <React.Fragment key={field.field}>
                                <Label>{field.description}{getRequiredElement(field)}</Label>
                                <ExtensionFieldSearch
                                    field={field}
                                    initialValue={props.staticData[field.field] ?? ''}
                                    onChange={(value) => {
                                        if (props.staticData[field.field] == value) return
                                        if (props.onChange) {
                                            const staticData = {...props.staticData};
                                            const subOrdinateFields = props.additionalFields.filter((f) => f.parentField === field.field);
                                            if (subOrdinateFields.length > 0) {
                                                subOrdinateFields.forEach((f) => {
                                                    staticData[f.field] = '';
                                                })
                                            }
                                            props.onChange({...staticData, [field.field]: value})
                                        }
                                    }
                                    }
                                    parentFieldValue={getParentFieldValue(field)}
                                    extensionId={workingId}
                                /></React.Fragment>
                        }

                        return <ExtensionDependentOption
                            key={field.field}
                            parentFieldValue={getParentFieldValue(field)}
                            initialValue={props.staticData[field.field] ?? ''}
                            optionsField={field}
                            {...batchPropsFor(field)}
                            onAutoSelect={applyAutoSelect}
                            onChange={(fieldKey, value) => {
                                if (props.staticData[fieldKey] == value) return
                                if (props.onChange) {
                                    const staticData = {...props.staticData};
                                    const subOrdinateFields = props.additionalFields.filter((f) => f.parentField === fieldKey);
                                    if (subOrdinateFields.length > 0) {
                                        subOrdinateFields.forEach((f) => {
                                            staticData[f.field] = '';
                                        })
                                    }
                                    props.onChange({...staticData, [field.field]: value})
                                }
                            }
                            }
                            extensionId={workingId}
                            readOnly={!isDependentFieldAccessible(field)}
                            onLoading={(value) => {
                                if(value){
                                    setLoadingFields((a) => a.concat(field.field))
                                }
                                else{
                                    setLoadingFields((a) => a.filter(f => f!= field.field))
                                }
                            }}
                        />

                    case 'installOption':
                        return <ExtensionInstallOption
                            key={field.field}
                            initialValue={props.staticData[field.field] ?? ''}
                            optionsField={field}
                            onAutoSelect={applyAutoSelect}
                            onChange={(fieldKey, value) => {
                                if (props.staticData[fieldKey] == value) return
                                if (props.onChange) {
                                    const staticData = {...props.staticData};
                                    const subOrdinateFields = props.additionalFields.filter((f) => f.parentField === fieldKey);
                                    if (subOrdinateFields.length > 0) {
                                        subOrdinateFields.forEach((f) => {
                                            staticData[f.field] = '';
                                        })
                                    }
                                    props.onChange({...staticData, [field.field]: value})
                                }
                            }
                            }
                            extensionId={workingId}
                        />
                    case 'flag':
                        return (
                            <div key={`extension-flag-${field.field}`} style={{paddingTop: "10px"}}>
                                <Checkbox checked={props.staticData[field.field] == 'true'}
                                          label={field.description}
                                          onChange={(fieldKey, value) => {
                                              if (props.staticData[field.field] == value?.toString()) return
                                              if (props.onChange) {
                                                  const staticData = {...props.staticData};
                                                  const subOrdinateFields = props.additionalFields.filter((f) => f.parentField === fieldKey);
                                                  if (subOrdinateFields.length > 0) {
                                                      subOrdinateFields.forEach((f) => {
                                                          staticData[f.field] = '';
                                                      })
                                                  }
                                                  props.onChange({
                                                      ...staticData,
                                                      [field.field]: value?.toString() ?? 'false'
                                                  })
                                              }
                                          }
                                          }
                                />
                            </div>
                        );
                    case 'dynamicFieldMap':
                        return renderDynamicFieldMap(field);
                    default:
                        return null;
                }
            })}</div>)})
} </div>)}

interface IExtensionStaticDataFieldProps {
    field: IAdditionalData;
    value: string;
    onChange: (value: string) => void;
    /**
     * Every keystroke, for UI that has to react before the value is committed.
     *
     * Separate from onChange on purpose: with updateOnDisengage the value is saved on blur, and moving the
     * save to each keystroke would mean a PUT per character. This says what the box currently reads,
     * nothing more.
     */
    onLiveChange?: (value: string) => void;
    updateOnDisengage?: boolean;
    tokenSuggestions?: ITokenSuggestion[];
    /**
     * Deep-link identifier, `extension.<sourceExtensionId>.<field>`. Built by the form rather than
     * registered up front, because these fields come from each extension's own schema.
     */
    settingId?: string;
    /**
     * Keep the red required asterisk but say nothing further.
     *
     * A form where six credentials are blank used to render six red boxes each announcing "Field is
     * required", which reads as six problems rather than one unfinished form. Only the first one still
     * outstanding makes the case; the rest are marked required and left quiet.
     */
    suppressRequiredMessage?: boolean;
}

/**
 * Marks the field so documentation can link to it, then renders it exactly as before. Kept as a
 * wrapper so the field-type switch below stays untouched.
 */
const ExtensionStaticDataField: React.FC<IExtensionStaticDataFieldProps> = (props) =>
    props.settingId
        ? <div data-setting-id={props.settingId}><ExtensionStaticDataFieldControl {...props} /></div>
        : <ExtensionStaticDataFieldControl {...props} />;

const ExtensionStaticDataFieldControl: React.FC<IExtensionStaticDataFieldProps> = (props) => {
    const [value, setValue] = React.useState<string>(props.value);
    useEffect(() => {
        if (value !== props.value) {
            setValue(props.value)
        }
    }, [props.value]);
    const currentValue = value;
    // Every control below already routes its edits through this, so reporting from here covers all of
    // them rather than each branch having to remember.
    const setCurrentValue = (next: string) => {
        setValue(next);
        props.onLiveChange?.(next);
    };

    const getRequiredElement = (field: IAdditionalData) => {
        if(!field.optional){
            return <div style={{display: "inline-flex", color: "rgb(164, 38, 44)", paddingLeft: "6px", paddingRight: "12px"}}>*</div>
        }
        return <></>
    }
    const getErrorMessage = (value: string): string => {
        if (props.suppressRequiredMessage) return '';
        return value.length < 1 && !props.field.optional ? 'Field is required' : '';
    };
    // Offer token autosuggest only where the backend will actually tokenize the value (field.supportsTokens) and
    // suggestions were supplied by the caller. Masked/protected fields are excluded — they aren't free text.
    const tokensEnabled = (props.tokenSuggestions?.length ?? 0) > 0
        && props.field.supportsTokens === true
        && !props.field.protected
        && !props.field.mask;
    switch (props.field.dataType) {
        case 'boolean':
            return <Checkbox
                label={props.field.description}
                checked={currentValue === "true"}
                onChange={(e, d) => props.onChange(d ? "true" : "false")}
            />
        case 'catalog':
            return <ExtensionCatalogSelection
                label={props.field.description}
                catalog={currentValue}
                onChange={props.onChange}
            />
        case 'integer':
            return <div>
                <Label>{props.field.description}{getRequiredElement(props.field)}</Label>
                <SpinButton 
                    value={currentValue}
                    min={0}
                    step={1}
                    max={props.field.maxValue}
                    onChange={(e, d) => {
                        if(d) {
                            setCurrentValue(d)
                            if (!props.updateOnDisengage) props.onChange(d)
                        }
                    }}
                    onBlur={(e) => {
                        if (props.updateOnDisengage) {
                            props.onChange(e.target.value)
                        }
                    }
                    }
                />
                {!props.field.optional && !currentValue && <p className={'field-error-message'}>Field is required</p>}
            </div>
        case 'multiLineText':
            if (tokensEnabled) {
                return <TokenAutosuggestField
                    toolTip={props.field.toolTip}
                    multiline={true}
                    rows={4}
                    onGetErrorMessage={getErrorMessage}
                    placeholder={props.field.optional ? undefined : `Value required for ${props.field.description}`}
                    label={props.field.description}
                    value={props.value}
                    onChange={props.onChange}
                    updateOnDisengage={props.updateOnDisengage}
                    required={!props.field.optional}
                    suggestions={props.tokenSuggestions ?? []}
                />
            }
            return <DataField
                toolTip={props.field.toolTip}
                multiline={true}
                rows={4}
                onGetErrorMessage={getErrorMessage}
                placeholder={props.field.optional ? undefined : `Value required for ${props.field.description}`}
                label={props.field.description}
                value={currentValue}
                onChange={(e, d) => {
                    setCurrentValue(d ?? '')
                    if (!props.updateOnDisengage) props.onChange(d ?? '')
                }}
                onBlur={(e) => {
                    if (props.updateOnDisengage) {
                        props.onChange(e.target.value)
                    }
                }}
                required={!props.field.optional}
            />
        case 'password':
        case 'string':
        case 'text':
        default:
            if (tokensEnabled) {
                return <TokenAutosuggestField
                    toolTip={props.field.toolTip}
                    placeholder={props.field.optional ? undefined : `Value required for ${props.field.description}`}
                    label={props.field.description}
                    value={props.value}
                    onChange={props.onChange}
                    updateOnDisengage={props.updateOnDisengage}
                    required={!props.field.optional}
                    suggestions={props.tokenSuggestions ?? []}
                />
            }
            return <DataField
                    toolTip={props.field.toolTip}
                    maskFormat={props.field.maskFormat}
                    maskChar={props.field.maskChar}
                    mask={props.field.mask}
                    placeholder={props.field.optional ? undefined : `Value required for ${props.field.description}`}
                    label={props.field.description}
                    // Without this DataField falls back to its own required check, which announces every
                    // empty field — the string and password types are most credential fields, so the
                    // "only the first one speaks" rule did nothing until they routed through here too.
                    onGetErrorMessage={getErrorMessage}
                    type={props.field.protected ? "password" : "text"}
                    onChange={(e, d) => {
                        setCurrentValue(d ?? '')
                        if (!props.updateOnDisengage) props.onChange(d ?? '')
                    }}
                    value={currentValue}
                    onBlur={(e) => {
                        if (props.updateOnDisengage) {
                            props.onChange(e.target.value)
                        }
                    }
                    }
                    required={!props.field.optional}
                />
    }
}

interface IExtensionCatalogSelection {
    label?: string;
    catalog?: string;
    onChange: (catalog: string) => void;
    disabled?: boolean;
}

const ExtensionCatalogSelection: React.FC<IExtensionCatalogSelection> = (props) => {
    const getCatalogs = useGetCatalogsQuery();
    return (
        <Dropdown
            label={props.label ?? "Catalog"}
            options={getCatalogs.data?.map(c => ({
                key: c.name,
                text: c.name
            })) ?? []}
            disabled={getCatalogs.isFetching || props.disabled}
            placeholder={getCatalogs.isFetching ? 'Loading' : 'Select a catalog'}
            selectedKey={props.catalog}
            onChange={(e, d) => props.onChange(d?.key.toString() ?? '')}
        />
    );
}

interface IExtensionMappingFormProps {
    mappings: IImportMapping[];
    vpnIsMfp: boolean;
    onChange: (mappings: IImportMapping[], vpnIsMfp: boolean) => void;
    onError: (message: string | undefined) => void;
    staticData: { [key: string]: string };
    id: string;
    installedId?: string;
    hasRequiredFields?: boolean;
}

const ExtensionMappingForm: React.FC<IExtensionMappingFormProps> = (props) => {
    const [getSample] = useLazyGetImportSampleQuery({pollingInterval: 0})
    const [loading, setLoading] = React.useState(false);
    const [sampleData, setSampleData] = React.useState<string[][]>([]);
    const [runImportExtension] = useRunImportExtensionMutation()

    const run = async () => {
        setLoading(true);
        props.onError(undefined);
        const sample = await getSample({extensionId: props.id, request: {additionalFields: props.staticData, installedExtensionId: props.installedId}})
        if (sample.data?.columns) {
            setSampleData(sample.data.columns);
        } else {
            props.onError(sample.data?.error ?? 'An error occurred loading the sample data')
        }
        setLoading(false);
    }

    const runImport = async () => {
        setLoading(true);
        props.onError(undefined);
        if(props.installedId) {
            runImportExtension({extensionId: props.installedId}).unwrap().then(a => {
                if(!a.success){
                    props.onError(a.message ?? "An error occured running the import")
                }
                setLoading(false)
            })
        }
        setLoading(false);
    }
    
    const canImportNow = () => {
        let can = true
        if(props.mappings.findIndex(a => a.field === 'manufacturerPartNumber')  === -1){
            can = false
        }
        if(props.mappings.findIndex(a => a.field === 'vendorPartNumber')  === -1){
            can = false
        }
        if(props.mappings.findIndex(a => a.field === 'name')  === -1){
            can = false
        }
        if(props.mappings.findIndex(a => a.field === 'cost')  === -1){
            can = false
        }
        return can
    }

    return <>
        <PrimaryButton
            onClick={run}
            text={!loading ? 'Load Sample' : 'Loading...'}
            disabled={loading || !props.hasRequiredFields}
        />
        <PrimaryButton
            onClick={runImport}
            text={!loading ? 'Import Now' : 'Loading...'}
            disabled={loading || !canImportNow() || !props.hasRequiredFields}
        />
        <Checkbox
            label="VPN is MFP"
            checked={props.vpnIsMfp}
            onChange={(e, d) => {
                props.onChange(props.mappings, d ?? false)
            }}
        />
        <MappingTable
            transpose
            vpnIsMfp={props.vpnIsMfp}
            sampleData={sampleData}
            initialMapping={props.mappings}
            onChange={(m) => props.onChange(m, props.vpnIsMfp)}
        />
    </>
}

interface IExtensionFolioInstallButtonsProps {
    extensionFolio?: IInstalledExtensionFolio;
    onValidate: (response: IActionResponse) => void;
    onClose?: () => void;
    /**
     * Field values to validate instead of the folio's stored ones. The validate-first flow (credentials
     * known bad, saves blocked) hands the unsaved edits through here so a validation can prove them
     * before anything is persisted.
     */
    overrideCommonFields?: { [key: string]: string };
}

export const ExtensionFolioInstallButtons: React.FC<IExtensionFolioInstallButtonsProps> = (props) => {
    const [validateInstalled] = useValidateExtensionFolioMutation();
    const [validating, setValidating] = React.useState(false);

    const validateExisting = async () => {
        const commonFields = props.overrideCommonFields ?? props.extensionFolio?.commonInstalledFields;
        if (props.extensionFolio?.id == null || commonFields == undefined) return;
        setValidating(true);
        if (props.extensionFolio.sourceExtensionFolio?.id) {
            const result = await validateInstalled({
                id: props.extensionFolio.sourceExtensionFolio.id,
                request: {
                    additionalData: commonFields,
                    folioId: props.extensionFolio.id,
                    validationActions: props.extensionFolio.enabledSubExtensions
                }
            });
            if (IsMutationSuccess<IActionResponse>(result)) {
                props.onValidate(result.data);
            } else if (IsMutationError(result)) {
                props.onValidate({success: false, message: 'Failed to communicate with API', id: ''})
            }
        }
        setValidating(false);
    }

    return (
        <Stack horizontal tokens={{childrenGap: '1em', padding: '1em'}}>
            {props.extensionFolio?.hasValidation && <StackItem grow={1}>
                <DefaultButton
                    style={{width: '100%'}}
                    text={!validating ? 'Validate' : 'Validating...'}
                    onClick={validateExisting}
                    disabled={validating}
                />
            </StackItem>}
            {props.onClose &&
                <StackItem grow={1}>
                    <DefaultButton
                        onClick={props.onClose}
                        style={{width: '100%'}}
                        text={'Close'}
                    />
                </StackItem>
            }
        </Stack>
    )
}

interface IExtensionInstallButtonsProps {
    request?: IExtensionInstallRequest;
    installed?: string;
    extension?: IExtension;
    isNew?: boolean;
    onSaved: () => void;
    onValidate: (response: IValidateExtensionResponse) => void;
    onSaveError: (error: string) => void;
    onClose: () => void;
}

export const ExtensionInstallButtons: React.FC<IExtensionInstallButtonsProps> = (props) => {
    const [validateExtension] = useValidateExtensionSettingsMutation();
    const [validateInstalled] = useValidateInstalledExtensionMutation();
    const [install] = useInstallExtensionMutation();
    const [update] = usePutInstalledExtensionMutation();
    const [validating, setValidating] = React.useState(false);

    const validateNew = async () => {
        if (props.extension?.id == null || props.request == null) return;
        setValidating(true);
        const result = await validateExtension({id: props.extension.id, request: props.request});
        if (IsMutationSuccess<IValidateExtensionResponse>(result)) {
            props.onValidate(result.data);
        } else if (IsMutationError(result)) {
            props.onValidate({success: false, message: 'Failed to communicate with API'})
        }
        setValidating(false);
    }

    const validateExisting = async () => {
        if (props.installed == null || props.request == null) return;
        setValidating(true);
        const result = await validateInstalled({id: props.installed, request: props.request});
        if (IsMutationSuccess<IValidateExtensionResponse>(result)) {
            props.onValidate(result.data);
        } else if (IsMutationError(result)) {
            props.onValidate({success: false, message: 'Failed to communicate with API'})
        }
        setValidating(false);
    }

    const save = async () => {
        if (props.request == null) {
            props.onSaveError('Request not found');
            return;
        }
        if (props.isNew) {
            if (props.extension?.id == null) {
                props.onSaveError('Extension ID not found');
                return;
            }
            const result = await install({request: props.request, extensionId: props.extension.id});
            if (IsMutationSuccess<IActionResponse>(result)) {
                if (result.data.success) {
                    props.onSaved();
                } else {
                    props.onSaveError(result.data.message);
                }
            } else {
                props.onSaveError('Failed to communicate with API');
            }
        } else {
            if (props.installed == null) {
                props.onSaveError('Installed extension ID not found');
                return;
            }
            const result = await update({extensionId: props.installed, request: props.request});
            if (IsMutationSuccess<IActionResponse>(result)) {
                if (result.data.success) {
                    props.onSaved();
                } else {
                    props.onSaveError(result.data.message);
                }
            } else {
                props.onSaveError('Failed to communicate with API');
            }
        }
    }

    return (
        <Stack horizontal tokens={{childrenGap: '1em', padding: '1em'}}>
            <StackItem grow={1}>
                <PrimaryButton
                    style={{width: '100%'}}
                    text={'Save'}
                    onClick={save}
                />
            </StackItem>
            {props.extension?.hasValidation && <StackItem grow={1}>
                <DefaultButton
                    style={{width: '100%'}}
                    text={!validating ? 'Validate' : 'Validating...'}
                    onClick={props.isNew ? validateNew : validateExisting}
                    disabled={validating}
                />
            </StackItem>}
            <StackItem grow={1}>
                <DefaultButton
                    onClick={props.onClose}
                    style={{width: '100%'}}
                    text={'Close'}
                />
            </StackItem>
        </Stack>
    )
}

export default ExtensionInstallForm;